import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Search } from 'lucide-react';
import BlogCard from '../components/blog/BlogCard';
import { loadBlogPosts } from '../utils/markdown';
import settings from '../constants/settings';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);

  // Load blog posts on component mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const fetchedPosts = await loadBlogPosts();
        // Sort posts by date (newest first)
        const sortedPosts = fetchedPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(sortedPosts);
        setFilteredPosts(sortedPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Extract all unique tags from posts
  const allTags = posts.reduce((tags, post) => {
    if (!post.tags) return tags;
    
    const postTags = Array.isArray(post.tags) ? post.tags : [post.tags];
    postTags.forEach(tag => {
      if (!tags.includes(tag)) {
        tags.push(tag);
      }
    });
    
    return tags;
  }, []);

  // Handle search and filter
  useEffect(() => {
    let result = posts;
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(post => {
        return (
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.author.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    
    // Filter by tag
    if (selectedTag) {
      result = result.filter(post => {
        const postTags = Array.isArray(post.tags) ? post.tags : [post.tags];
        return postTags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase());
      });
    }
    
    setFilteredPosts(result);
  }, [searchTerm, selectedTag, posts]);

  return (
    <div className="blog page-container mt-[90px]">
      <Helmet>
        <title>Blog | Pintar Pilih Kuliah</title>
        <meta name="description" content="Artikel, tips, dan informasi terkini seputar beasiswa internasional, persiapan kuliah di luar negeri, dan pengalaman alumni." />
      </Helmet>
      
      {/* Blog Header */}
      <section className="py-12 bg-[#FDF6F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-serif font-bold mb-4 text-gray-900">
              Blog #PintarPilihKuliah
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Artikel, tips, dan informasi terkini seputar beasiswa internasional, persiapan kuliah di luar negeri, dan pengalaman alumni.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="bg-white border border-gray-300 text-gray-900 text-sm rounded-full block w-full pl-10 p-3 focus:outline-none focus:ring-2 focus:ring-gold"
                placeholder="Cari artikel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Blog Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Tags Filter */}
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                selectedTag === '' ? 'bg-gold text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => setSelectedTag('')}
            >
              Semua
            </button>
            {allTags.map((tag, index) => (
              <button
                key={index}
                className={`px-3 py-1 rounded-full text-sm ${
                  selectedTag === tag ? 'bg-gold text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-gray-900 mb-2">Tidak ada artikel yang ditemukan</h3>
              <p className="text-gray-600">
                Coba ubah kata kunci pencarian atau filter yang dipilih.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <BlogCard key={index} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-[#f6d39d] text-dark-navy">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-serif font-bold mb-6">Masih Bingung Menentukan Pilihan?</h2>
            <p className="text-lg mb-8">
              Dapatkan konsultasi gratis dengan tim #PintarPilihKuliah untuk mendapatkan arahan dan informasi terkait peluang beasiswa luar negeri.
            </p>
            <a
              href={settings.wa_consultant_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white hover:bg-dark-navy text-dark-navy hover:text-white rounded-md px-6 py-3 font-medium transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2 text-green-600"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              </svg>
              Konsultasi Gratis
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
